import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Table from 'components/widgets/Table';
import {
    makeStyles, Dialog, DialogContent, AppBar, Toolbar, Typography, Button,
    useMediaQuery,
} from '@material-ui/core';
import clsx from 'clsx';
import { useTheme } from '@material-ui/core/styles';
import { AddIcon } from 'components/icons';
import TableUtils from 'utils/TableUtils';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import Header from 'components/widgets/Header';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InputSearch from 'components/widgets/InputSearch';
import DialogActions from 'components/widgets/modal/DialogActions';
import VehiclesInCards from 'components/modules/service/serviceInvoice/create/VehiclesInCards';
import useCustomerAndVehicleMethods from 'components/modules/service//serviceInvoice/hooks/useCustomerAndVehicleMethods';
import VinScanner from 'components/widgets/inventory/VinScanner';

const useStyle = makeStyles((theme) => AccountingStyles.checkActionList(theme));
const ownStyle = makeStyles((theme) => (
    {
        dialogContent: {
            height: '62vh',
            '& > div:first-child': {
                height: '62vh',
                [theme.breakpoints.down('sm')]: {
                    height: '100%',
                },
            },
            padding: 0,
            overflow: 'hidden',
        },
        header: {
            [theme.breakpoints.down('sm')]: {
                '& > div': {
                    marginTop: '10px',
                    '& > div > div': {
                        width: '100%',
                    },
                    '& > div > button': {
                        marginTop: '5px',
                        marginBottom: '5px',
                    },
                },
            },
        },
        mainTable: {
            height: 'calc(100vh - 420px)',
        },
        search: {
            borderRadius: 10,
            width: 350,
            '& .form-control': {
                height: 'calc(1.5em + 0.5rem + 2px)',
                padding: '0.25rem 0.5rem',
                fontSize: '0.875rem',
                lineHeight: '1.5',
                borderRadius: '0.2rem',
            },
        },
        scanner: {
            display: 'flex',
            '& > div:nth-child(1) > div input': {
                height: '36px',
            },
        },
    }
));

const VehiclesByCustomerList = (props) => {
    const classes = {
        ...useStyle(),
        ...ownStyle(),
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const {
        appBar, centerItems, title,
        dialogContent, search,
    } = classes;

    const {
        inHouse, code, onClose, onSelectVehicle,
        onClickNewVehicleButton,
    } = props;

    const [selectedRecord, onSelectedRecord] = useState(null);
    const [searchData, setSearchData] = useState({
        key: TableUtils.generateUUID(),
        search: '',
    });

    const {
        getVehicleData, loadingVehicleInfo,
        vehicles, getVehicleColumns,
    } = useCustomerAndVehicleMethods({ inHouse });

    useEffect(() => {
        if (code) {
            getVehicleData({
                variables: {
                    code,
                    inHouse,
                    search: '',
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSearch = (value) => {
        setSearchData({
            key: TableUtils.generateUUID(),
            search: value,
        });

        getVehicleData({
            variables: {
                code,
                inHouse,
                search: value?.trim() ?? '',
            },
        });
    };

    return (
        <>
            <Dialog
                open
                onClose={onClose}
                maxWidth={inHouse ? 'lg' : 'md'}
                fullScreen={isMobile}
                fullWidth={!isMobile}
                disableBackdropClick
                disableEscapeKeyDown
                scroll="paper"
            >
                <AppBar className={appBar}>
                    <Toolbar className={centerItems}>
                        <Typography variant="h6" className={title}>
                            Select Vehicle
                        </Typography>
                        <div className={centerItems}>
                            <IconButton edge="start" color="inherit" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </Toolbar>
                </AppBar>
                <DialogContent className={dialogContent}>
                    <div className={classes.main}>
                        <Header className={classes.header}>
                            <div className={classes.scanner}>
                                <InputSearch
                                    autoFocus
                                    initialSearch={searchData.search}
                                    key={searchData.key}
                                    customClasses={clsx(search)}
                                    onSearch={onSearch}
                                />
                                <VinScanner
                                    scanStock
                                    shortButton
                                    loading={loadingVehicleInfo}
                                    onSearchVehicle={onSearch}
                                />
                            </div>
                            {!inHouse && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<AddIcon />}
                                    size="small"
                                    onClick={onClickNewVehicleButton}
                                >
                                    Add new Vehicle
                                </Button>
                            ) }
                        </Header>
                        {isMobile && (
                            <VehiclesInCards
                                classes={classes}
                                records={vehicles ?? []}
                                onSelect={(record) => onSelectVehicle(record)}
                                inHouse={inHouse}
                                loading={loadingVehicleInfo}
                            />
                        )}
                        {!isMobile && (
                            <div className={classes.mainTable}>
                                <Table
                                    cursor="default"
                                    load={loadingVehicleInfo}
                                    totalvehicles={vehicles.length || 0}
                                    data={loadingVehicleInfo ? [] : vehicles}
                                    columns={getVehicleColumns()}
                                    className="-highlight"
                                    rowSelected
                                    enableRowHover
                                    getTrProps={(_, rowInfo) => ({
                                        onDoubleClick: () => {
                                            onSelectedRecord(rowInfo.original);
                                        },
                                    })}
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
                {!isMobile && (
                    <DialogActions
                        titlePrimary="Ok"
                        onClickSecondary={onClose}
                        onClickPrimary={() => onSelectVehicle(selectedRecord)}
                        disablePrimaryButton={!selectedRecord}
                    />
                )}
            </Dialog>
        </>
    );
};

VehiclesByCustomerList.propTypes = {
    code: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]).isRequired,
    inHouse: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onSelectVehicle: PropTypes.func.isRequired,
    onClickNewVehicleButton: PropTypes.func,
};

VehiclesByCustomerList.defaultProps = {
    inHouse: false,
    onClickNewVehicleButton: () => {},
};

export default VehiclesByCustomerList;
